import React from "react";
import { Button } from "@/components/ui/button";
import { FileEdit, Trash2, PackageX } from "lucide-react";
import { Package, Status } from "@/types";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";

interface PackageTableActionsProps {
  pkg: Package;
  isOlderThan30Days: boolean;
  onMarkDelivered: (pkg: Package) => void;
  onUndoDelivery: (pkg: Package) => void;
  onViewInfo: (pkg: Package) => void;
  onDelete: (pkg: Package) => void;
  onDiscard: (pkg: Package) => void;
  onUndoDelete: (id: number) => void;
  onUndoDiscard: (id: number) => void;
  onEdit: (pkg: Package) => void;
}

const PackageTableActions: React.FC<PackageTableActionsProps> = ({
  pkg,
  isOlderThan30Days,
  onMarkDelivered,
  onUndoDelivery,
  onViewInfo,
  onDelete,
  onDiscard,
  onUndoDelete,
  onUndoDiscard,
  onEdit,
}) => {
  const mainAction = pkg.status === "Pending" ? (
    <Button
      variant="default"
      size="sm"
      className="inline-flex items-center w-full"
      onClick={() => onMarkDelivered(pkg)}
    >
      Mark Delivered
    </Button>
  ) : pkg.status === "Deleted" && pkg.is_deleted ? (
    <Button
      variant="outline"
      size="sm"
      className="inline-flex items-center w-full"
      onClick={() => onUndoDelete(pkg.id)}
    >
      Undo Changes
    </Button>
  ) : pkg.status === "Discarded" ? (
    <Button
      variant="outline"
      size="sm"
      className="inline-flex items-center w-full"
      onClick={() => onUndoDiscard(pkg.id)}
    >
      Undo Changes
    </Button>
  ) : (
    <Button
      variant="outline"
      size="sm"
      className="inline-flex items-center w-full"
      onClick={() => onUndoDelivery(pkg)}
    >
      Reverse Changes
    </Button>
  );

  return (
    <div className="flex flex-col gap-2">
      {mainAction}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="w-full mt-1">Actions</Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuItem
                  onSelect={() => (pkg.status === 'Deleted' && pkg.is_deleted) || pkg.status === 'Discarded' || pkg.status === 'Delivered' ? undefined : onEdit(pkg)}
                  disabled={pkg.status === 'Deleted' && pkg.is_deleted || pkg.status === 'Discarded' || pkg.status === 'Delivered'}
                  className="flex items-center gap-2"
                >
                  <FileEdit size={16} className={(pkg.status === 'Deleted' && pkg.is_deleted) || pkg.status === 'Discarded' || pkg.status === 'Delivered' ? 'opacity-40' : 'text-primary group-hover:opacity-100'} />
                  Edit
                </DropdownMenuItem>
              </TooltipTrigger>
              <TooltipContent>Edit</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuItem
                  onSelect={() => pkg.status === 'Deleted' && pkg.is_deleted ? onUndoDelete(pkg.id) : pkg.status === 'Discarded' || pkg.status === 'Delivered' ? undefined : onDelete(pkg)}
                  disabled={pkg.status === 'Discarded' || pkg.status === 'Delivered'}
                  className={pkg.status === 'Deleted' && pkg.is_deleted ? 'text-red-500' : pkg.status === 'Discarded' || pkg.status === 'Delivered' ? 'opacity-40' : ''}
                >
                  <Trash2 size={16} />
                  Delete
                </DropdownMenuItem>
              </TooltipTrigger>
              <TooltipContent>Delete</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuItem
                  onSelect={() => pkg.status === 'Discarded' ? onUndoDiscard(pkg.id) : pkg.status === 'Deleted' && pkg.is_deleted || pkg.status === 'Delivered' ? undefined : onDiscard(pkg)}
                  disabled={pkg.status === 'Deleted' && pkg.is_deleted || pkg.status === 'Delivered'}
                  className={pkg.status === 'Discarded' ? 'text-orange-500' : pkg.status === 'Deleted' && pkg.is_deleted || pkg.status === 'Delivered' ? 'opacity-40' : ''}
                >
                  <PackageX size={16} />
                  Discard
                </DropdownMenuItem>
              </TooltipTrigger>
              <TooltipContent>Discard</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default PackageTableActions;
